import { zodResolver } from "@hookform/resolvers/zod";
import classNames from "classnames";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { ErrorMessage } from "../../utils/ErrorMessage";
import Button from "../Common/Buttons/Button";
import PasswordInputField from "../Common/Input/PasswordInputField";

export const LoginFormScheme = z.object({
  password: z
    .string({
      required_error: "Password is required.",
    })
    .trim()
    .min(1, { message: "Password is required." }),
  username: z
    .string({
      required_error: "Username is required.",
    })
    .trim()
    .min(1, { message: "Username is required." }),
});

export type LoginFormInput = z.infer<typeof LoginFormScheme>;

type LoginFormProps = {
  onSubmit: (data: LoginFormInput) => void;
  error: string | null;
  loading: boolean;
  username?: string;
  password?: string;
  resetError: () => void;
};

export const LoginForm = ({
  onSubmit,
  error,
  loading,
  username,
  password,
  resetError,
}: LoginFormProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const methods = useForm<LoginFormInput>({
    resolver: zodResolver(LoginFormScheme),
    mode: "onChange",

    defaultValues: {
      username,
      password,
    },
  });

  const { handleSubmit, formState, watch } = methods;

  const onFormSubmit = handleSubmit(onSubmit);

  const userName = watch("username");
  const pass = watch("password");

  useEffect(() => {
    if (username) {
      methods.setValue("username", username, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
    }

    if (password) {
      methods.setValue("password", password, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
    }
  }, [username, password, methods]);

  useEffect(() => {
    if (error) {
      resetError();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userName, pass]);
  return (
    <div className="md:shadow-lg md:bg-white rounded-[24px] md:p-[32px]">
      <h1
        aria-label="login-title"
        className="block font-semibold text-[32px] leading-[127%]"
      >
        {t("auth.login.title")}
      </h1>
      <p className="py-[16px] text-[18px] leading-[127%]">
        {t("auth.login.description")}
      </p>

      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
            className="p-[12px] bg-[#E53A14] rounded-[8px] mb-[32px]"
          >
            <p className="first-letter:uppercase leading-[127%] font-normal text-[16px] text-white">
              {error}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} data-testid="login-form">
          <div className="flex flex-col space-y-[16px] text-[18px] mb-[32px]">
            <Controller
              control={methods.control}
              name="username"
              render={({ field: { value, onChange } }) => (
                <div>
                  <input
                    type="text"
                    aria-label="username"
                    id="username"
                    name="username"
                    className="w-full px-[12px] py-[14px] border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black text-black bg-[#F8F8F8]"
                    placeholder={t("auth.login.username_placeholder")}
                    onChange={onChange}
                    value={value}
                  />

                  <ErrorMessage fieldName="username" />
                </div>
              )}
            />

            <Controller
              control={methods.control}
              name="password"
              render={({ field: { value, onChange } }) => (
                <PasswordInputField
                  id="password"
                  type="password"
                  name="password"
                  value={value}
                  aria-label="password"
                  onChange={onChange}
                  label="Password"
                  placeholder={t("auth.login.password_placeholder")}
                  isNewPassword={false}
                />
              )}
            />

            <p
              className={classNames(
                "w-fit text-[14px]  cursor-pointer text-brand"
              )}
              onClick={() => navigate("/forgot-password")}
            >
              {t("auth.login.forgot_password")}
            </p>
          </div>

          <Button
            iconVisibility="hide"
            ariaLabel="login"
            label={t("auth.login.login_button")}
            loading={loading}
            className={classNames(
              "!bg-brand-default",
              !formState.isValid &&
                "opacity-50 cursor-not-allowed bg-brand-default"
            )}
            handleClick={() => void onFormSubmit()}
          />
        </form>
      </FormProvider>
    </div>
  );
};
